/**
 * OrdersRealtimeProvider
 * 
 * Single realtime subscription for all order changes.
 * Mounted at AppShellContent level (authenticated routes only).
 * 
 * Responsibilities:
 * - One channel per table (orders)
 * - Event classification and targeted cache patching
 * - Connection state tracking with fallback polling
 * - Subscriber notification for consumers (e.g., LivePulse)
 * 
 * Dependencies: user.id, queryClient only
 * NO: t(), theme, route params, sidebar state
 */

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeContext, ConnectionState, OrderChangeCallback, OrderChangePayload } from "./RealtimeContext";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { Order } from "@/types/order";
import type { Product } from "@/types/inventory";

// Channel names for unified sync
const ORDERS_CHANNEL = 'orders-unified-sync';
const PRODUCTS_CHANNEL = 'products-stock-sync';

// Fallback polling interval when connection is degraded (60s safety net)
const FALLBACK_POLLING_INTERVAL = 60 * 1000;

type ChangeType = 'insert' | 'status' | 'note' | 'session' | 'delete' | 'minor';

interface OrdersRealtimeProviderProps {
  children: ReactNode;
}

export function OrdersRealtimeProvider({ children }: OrdersRealtimeProviderProps) {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const productsChannelRef = useRef<RealtimeChannel | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  
  // Subscriber registry for order change events
  const subscribersRef = useRef<Set<OrderChangeCallback>>(new Set());

  const subscribeToChanges = useCallback((callback: OrderChangeCallback) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  // Notify all subscribers of an order change
  const notifySubscribers = useCallback((payload: OrderChangePayload) => {
    subscribersRef.current.forEach(cb => {
      try {
        cb(payload);
      } catch (err) {
        console.error('[OrdersRealtime] Subscriber error:', err);
      }
    });
  }, []);

  // Main subscription effect
  useEffect(() => {
    // No user = degraded state (not authenticated)
    if (!user?.id) {
      setConnectionState('degraded');
      return;
    }

    // Cleanup previous channel if it exists (e.g., user changed)
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const ordersChannel = supabase
      .channel(ORDERS_CHANNEL)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          handleOrderChange(payload, queryClient);
          notifySubscribers({
            eventType: payload.eventType as OrderChangePayload['eventType'],
            newOrder: (payload.new as Record<string, unknown>) || null,
            oldOrder: (payload.old as Partial<Record<string, unknown>>) || null,
          });
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          setConnectionState('live');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[OrdersRealtime] Channel error/timeout:', err);
          setConnectionState('degraded');
        }
      });

    channelRef.current = ordersChannel;

    // Products channel - stock sync for manager dashboard
    const productsChannel = supabase
      .channel(PRODUCTS_CHANNEL)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          handleProductStockChange(payload, queryClient);
        }
      )
      .subscribe();

    productsChannelRef.current = productsChannel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (productsChannelRef.current) {
        supabase.removeChannel(productsChannelRef.current);
        productsChannelRef.current = null;
      }
    };
  }, [user?.id, queryClient, notifySubscribers]);

  // Fallback polling when degraded
  useEffect(() => {
    if (connectionState !== 'degraded') return;

    const interval = setInterval(() => {
      // Only refresh critical queries - safety net
      queryClient.invalidateQueries({ queryKey: ['today-orders'] });
      queryClient.invalidateQueries({ queryKey: ['active-delivery-link'] });
    }, FALLBACK_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [connectionState, queryClient]);

  return (
    <RealtimeContext.Provider value={{ connectionState, subscribeToChanges }}>
      {children}
    </RealtimeContext.Provider>
  );
}

/**
 * Handles realtime order changes with event classification and cache patching.
 * Never throws - silently logs errors to prevent UI breakage.
 */
function handleOrderChange(
  payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>,
  queryClient: ReturnType<typeof useQueryClient>
) {
  try {
    const eventType = payload.eventType;
    const newOrder = payload.new as Order | null;
    const oldOrder = payload.old as Partial<Order> | null;

    // DELETE events - invalidate lists, don't try to patch
    if (eventType === 'DELETE') {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['today-orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
      return;
    }

    // For INSERT and UPDATE, we need the new order data
    if (!newOrder || !newOrder.id) {
      console.warn('[OrdersRealtime] Missing new order data:', payload);
      return;
    }

    const changeType = classifyChange(eventType, oldOrder, newOrder);

    // Apply changes based on classification
    switch (changeType) {
      case 'insert':
        // Don't prepend - just invalidate lists to avoid filter/ordering issues
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['today-orders'] });
        queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
        queryClient.invalidateQueries({ queryKey: ['hasAnyOrders'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        // Patch detail cache (for if user navigates to it quickly)
        patchOrderDetail(newOrder, queryClient);
        break;

      case 'status':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // Invalidate aggregates that depend on status
        queryClient.invalidateQueries({ queryKey: ['today-kpis'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
        queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        break;

      case 'note':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // No aggregate invalidation needed for notes
        break;

      case 'session':
        patchOrderDetail(newOrder, queryClient);
        patchTodayOrders(newOrder, queryClient);
        patchPaginatedOrders(newOrder, queryClient);
        // Invalidate delivery-related queries
        queryClient.invalidateQueries({ queryKey: ['delivery-stats'] });
        queryClient.invalidateQueries({ queryKey: ['session-orders'] });
        break;

      case 'minor':
        // Only patch detail - don't refresh lists for minor changes
        patchOrderDetail(newOrder, queryClient);
        break;

      default:
        patchOrderDetail(newOrder, queryClient);
    }
  } catch (err) {
    // Never throw inside realtime handler - UI still works via polling/refetch
    console.error('[OrdersRealtime] Error handling change:', err);
  }
}

/**
 * Classifies the type of change based on eventType and field diffs.
 */
function classifyChange(
  eventType: 'INSERT' | 'UPDATE' | 'DELETE',
  oldOrder: Partial<Order> | null,
  newOrder: Order
): ChangeType {
  if (eventType === 'INSERT') return 'insert';
  if (eventType === 'DELETE') return 'delete';

  // For UPDATE, compare specific fields
  if (oldOrder?.order_status !== newOrder.order_status) return 'status';
  if (oldOrder?.internal_notes !== newOrder.internal_notes) return 'note';
  if (oldOrder?.assigned_to !== newOrder.assigned_to) return 'session';

  return 'minor';
}

/**
 * Patches the individual order detail cache.
 */
function patchOrderDetail(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setQueryData(['order', order.id], order);
}

/**
 * Patches the today-orders list cache (flat Order[] array).
 * Only updates if order exists in cache - doesn't add new orders.
 */
function patchTodayOrders(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setQueryData<Order[]>(['today-orders'], (old) => {
    // Guard: Must be array
    if (!Array.isArray(old)) return old;
    
    // Only update if order exists in cache
    const exists = old.some(o => o.id === order.id);
    if (!exists) return old;
    
    return old.map(o => o.id === order.id ? order : o);
  });
}

/**
 * Patches paginated orders cache (object with orders array).
 * Iterates over all cached pages/filter combinations.
 * Only updates if order exists - doesn't add new orders.
 */
function patchPaginatedOrders(order: Order, queryClient: ReturnType<typeof useQueryClient>) {
  // setQueriesData iterates over all matching query keys
  queryClient.setQueriesData<{ orders: Order[]; totalCount: number }>(
    { queryKey: ['orders'] },
    (old) => {
      // Guard: Must have expected shape
      if (!old || !Array.isArray(old.orders)) return old;
      
      // Only update if order exists in this page's cache
      const exists = old.orders.some(o => o.id === order.id);
      if (!exists) return old;
      
      return {
        ...old,
        orders: old.orders.map(o => o.id === order.id ? order : o),
      };
    }
  );
}

/**
 * Handles realtime product stock changes.
 * Only patches cache when current_stock actually changed to avoid noisy writes.
 */
function handleProductStockChange(
  payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>,
  queryClient: ReturnType<typeof useQueryClient>
) {
  try {
    const newProduct = payload.new as Product | null;
    const oldProduct = payload.old as Partial<Product> | null;

    if (!newProduct?.id) return;

    // Only act if current_stock actually changed
    if (oldProduct && oldProduct.current_stock === newProduct.current_stock) return;

    // Patch individual product detail cache
    queryClient.setQueryData(['product', newProduct.id], (old: Product | undefined) => {
      if (!old) return old;
      return { ...old, current_stock: newProduct.current_stock, updated_at: newProduct.updated_at };
    });

    // Patch products list cache (all pages/filter combos)
    queryClient.setQueriesData<{ products: Product[]; totalCount: number }>(
      { queryKey: ['products'] },
      (old) => {
        if (!old || !Array.isArray(old.products)) return old;
        const exists = old.products.some(p => p.id === newProduct.id);
        if (!exists) return old;
        return {
          ...old,
          products: old.products.map(p =>
            p.id === newProduct.id
              ? { ...p, current_stock: newProduct.current_stock, updated_at: newProduct.updated_at }
              : p
          ),
        };
      }
    );
  } catch (err) {
    console.error('[OrdersRealtime] Error handling product stock change:', err);
  }
}
